import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import LandIcon from "assets/icons/ic_map_type_ground.svg";
import { BldConditionTypeEnum } from "common/enums/BldConditionTypeEnum";
import { StatusNameEnum } from "common/enums/StatusEnum";
import { dateToTermString } from "common/functions/estates";
import { differenceInDays } from "date-fns";
import { Estate } from "graphql/API";
import React, { useCallback, useMemo, useState } from "react";
import styles from "../PricingDialog.module.scss";
import { SortableColumn } from "./SortableColumn";

export const dspStatus = (status: string | null | undefined) => {
  return status === StatusNameEnum.公開 || status === StatusNameEnum.非公開
    ? "売出中"
    : status;
};

export const dspFullAddress = (estate: Estate) =>
  `${estate.prefecture ?? ""}${estate.city ?? ""}${estate.area ?? ""}`;

export const dspCompanyName = (estate: Estate) =>
  (estate.status === StatusNameEnum.公開 ||
  estate.status === StatusNameEnum.非公開
    ? estate.companyName?.split(" ")[0]
    : undefined) || "-";

export const dspEstatePrice = (estate: Estate) =>
  Math.round((estate?.estatePrice || 0) / 10) * 10;

export const dspTsuboArea = (estate: Estate) =>
  (estate?.tsuboArea || 0) - ((estate?.tsuboArea || 0) % 1);

const dspUnitPrice = (estate: Estate) =>
  Math.round((estate.unitPrice || 0) * 10) / 10;

export const updateAtForCompare = (estate: Estate) =>
  dspStatus(estate.status) === "売出中" ? "0" : (estate.updatedAt ?? "0");

export const dspUpdatedAt = (estate: Estate) => {
  const updatedAt = updateAtForCompare(estate);
  return updatedAt === "0" ? "-" : dateToTermString(updatedAt);
};

export const sellingDaysForCompare = (estate: Estate) =>
  dspStatus(estate.status) === "売出中"
    ? -1
    : estate.createdAt &&
        estate.updatedAt &&
        differenceInDays(estate.updatedAt, estate.createdAt) > 0
      ? differenceInDays(estate.updatedAt, estate.createdAt)
      : undefined;

export const dspSellingDays = (estate: Estate) => {
  const sellingDays = sellingDaysForCompare(estate);
  return sellingDays === -1
    ? "-"
    : sellingDays === undefined
      ? "不明"
      : `${sellingDays} 日`;
};

type ColumnName =
  | "status"
  | "fullAddress"
  | "companyName"
  | "estatePrice"
  | "tsuboArea"
  | "unitPrice"
  | "updatedAt"
  | "sellingDays";

export const LandTable = React.memo(
  (props: {
    allLandEstates: Estate[];
    selectedEstateIds: Set<string>;
    onSelectEstateClick: (estateId: string) => void;
  }) => {
    const [sortColumn, setSortColumn] = useState<ColumnName | undefined>(
      undefined
    );
    const [sortDirection, setSortDirection] = useState<
      "asc" | "desc" | undefined
    >(undefined);

    const onSortClick = useCallback(
      (column: ColumnName, sortDirection: "asc" | "desc") => {
        setSortColumn(column);
        setSortDirection(sortDirection);
      },
      []
    );

    const sortedAllLandEstates = useMemo(() => {
      return props.allLandEstates.toSorted((a, b) => {
        if (sortColumn === "status") {
          return (
            (dspStatus(a.status) ?? "").localeCompare(
              dspStatus(b.status) ?? ""
            ) * (sortDirection === "asc" ? 1 : -1)
          );
        }

        if (sortColumn === "fullAddress") {
          return (
            dspFullAddress(a).localeCompare(dspFullAddress(b)) *
            (sortDirection === "asc" ? 1 : -1)
          );
        }

        if (sortColumn === "companyName") {
          return (
            (dspCompanyName(a) ?? "").localeCompare(dspCompanyName(b) ?? "") *
            (sortDirection === "asc" ? 1 : -1)
          );
        }

        if (sortColumn === "estatePrice") {
          return (
            (dspEstatePrice(a) - dspEstatePrice(b)) *
            (sortDirection === "asc" ? 1 : -1)
          );
        }

        if (sortColumn === "tsuboArea") {
          return (
            (dspTsuboArea(a) - dspTsuboArea(b)) *
            (sortDirection === "asc" ? 1 : -1)
          );
        }

        if (sortColumn === "unitPrice") {
          return (
            (dspUnitPrice(a) - dspUnitPrice(b)) *
            (sortDirection === "asc" ? 1 : -1)
          );
        }

        if (sortColumn === "updatedAt") {
          return (
            updateAtForCompare(a).localeCompare(updateAtForCompare(b)) *
            (sortDirection === "asc" ? 1 : -1)
          );
        }

        if (sortColumn === "sellingDays") {
          return (
            ((sellingDaysForCompare(a) ?? 0) -
              (sellingDaysForCompare(b) ?? 0)) *
            (sortDirection === "asc" ? 1 : -1)
          );
        }

        return 0;
      });
    }, [props.allLandEstates, sortColumn, sortDirection]);

    return (
      <Accordion className={styles.accordion}>
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon className={styles.icon} />}
          aria-controls="land-content"
          id="land-header"
        >
          <Box className={`${styles.category} ${styles.small}`}>
            <Box
              component="img"
              src={LandIcon}
              alt="土地"
              className={styles.estateTypeIcon}
            />
            土地の過去事例：{sortedAllLandEstates.length} 件
          </Box>
        </AccordionSummary>
        <AccordionDetails className={styles.details}>
          {sortedAllLandEstates.length > 0 ? (
            <TableContainer className={styles.tableContainer}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.alignCenter}>
                      <Box>選択</Box>
                    </TableCell>
                    <TableCell>
                      <SortableColumn
                        column="status"
                        label="ステータス"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell>
                      <SortableColumn
                        column="fullAddress"
                        label="所在地"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell>
                      <SortableColumn
                        column="companyName"
                        label="業者名"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell className={styles.alignRight}>
                      <SortableColumn
                        column="estatePrice"
                        label="価格"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell className={styles.alignRight}>
                      <SortableColumn
                        column="tsuboArea"
                        label="広さ"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell className={styles.alignRight}>
                      <SortableColumn
                        column="unitPrice"
                        label="坪単価"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell> </TableCell>
                    <TableCell>
                      <SortableColumn
                        column="updatedAt"
                        label="掲載終了"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell className={styles.alignRight}>
                      <SortableColumn
                        column="sellingDays"
                        label="売出日数"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedAllLandEstates.map((estate) => {
                    const checked = props.selectedEstateIds.has(estate.id);

                    return (
                      <TableRow key={estate.id}>
                        <TableCell
                          className={`${styles.alignCenter} ${
                            checked ? styles.on : ""
                          }`}
                        >
                          <Switch
                            checked={checked}
                            onChange={() =>
                              props.onSelectEstateClick(estate.id)
                            }
                          />
                        </TableCell>
                        <TableCell className={checked ? styles.on : ""}>
                          <Chip
                            label={dspStatus(estate.status)}
                            className={
                              dspStatus(estate.status) === "売出中"
                                ? styles.publicStatusLabel
                                : styles.soldStatusLabel
                            }
                          />
                        </TableCell>
                        <TableCell className={checked ? styles.on : ""}>
                          {dspFullAddress(estate)}
                        </TableCell>
                        <TableCell className={checked ? styles.on : ""}>
                          {dspCompanyName(estate)}
                        </TableCell>
                        <TableCell
                          className={`${styles.alignRight} ${
                            checked ? styles.on : ""
                          }`}
                        >
                          {String(dspEstatePrice(estate))} 万
                        </TableCell>
                        <TableCell
                          className={`${styles.alignRight} ${
                            checked ? styles.on : ""
                          }`}
                        >
                          {String(dspTsuboArea(estate))} 坪
                        </TableCell>
                        <TableCell
                          className={`${styles.alignRight} ${
                            checked ? styles.on : ""
                          }`}
                        >
                          {String(dspUnitPrice(estate))} 万/坪
                        </TableCell>
                        <TableCell className={checked ? styles.on : ""}>
                          {estate.currentStatus && (
                            <Chip
                              label={estate.currentStatus}
                              className={styles.currentStatusLabel}
                            />
                          )}{" "}
                          {estate.isLot === 1 && (
                            <Chip label="分譲地" className={styles.lotLabel} />
                          )}{" "}
                          {estate.bldConditionType ===
                          BldConditionTypeEnum.建築条件あり ? (
                            <Chip
                              label="建築条件あり"
                              className={styles.currentStatusLabel}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell className={checked ? styles.on : ""}>
                          {dspUpdatedAt(estate)}
                        </TableCell>
                        <TableCell
                          className={`${styles.alignRight} ${
                            checked ? styles.on : ""
                          }`}
                        >
                          {dspSellingDays(estate)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box className={styles.noData}>※ 過去事例がありませんでした。</Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }
);
